import React, { useContext } from "react";
import classnames from "classnames";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { Action, Category } from "@/constants/events";
import StoreLocatorContext from "@/context/StoreLocatorContext";
import { Store } from "@/custom-types/StoreLocator";
import useDispensaryRouteGen from "@/hooks/dispensaries/useDispensaryRouteGen";
import { useEventTracker } from "@/hooks/useEventTracker";
import {
  getOrganicCardEventLabel,
  getSponsoredCardEventLabel,
} from "@/utils/storeLocator/eventTracking";

import ScheduleStatus from "@/components/botanic/ScheduleStatus";
import StarRating from "@/components/botanic/StarRating";
import AwardBadgeIcon from "@/components/Icons/award_badge.svg";
import Image from "@/components/Image";
import OfferLabel from "@/components/OfferLabel";
import TrackImpression from "@/components/TrackImpression";

import CTAButton from "./CTAButton";
import PickupLabel from "./PickupLabel";

const ScooterIcon = dynamic(() => import("@/components/Icons/scooter.svg"));

const StorefrontIcon = dynamic(
  () => import("@/components/Icons/storefront.svg"),
);

const StoreCard: React.FC<{
  isSponsored: boolean;
  slot: number;
  store: Store;
}> = ({ isSponsored, slot, store }) => {
  const { push } = useRouter();
  const { publishEvent } = useEventTracker();
  const dealHref = useDispensaryRouteGen(store).dealLink();
  const eventLabel = isSponsored
    ? getSponsoredCardEventLabel(slot)
    : getOrganicCardEventLabel(store.featureTier);

  const handleCardClick = () => {
    publishEvent({
      action: Action.click,
      category: Category.finder,
      dealId: store.mostRecentActiveDeal?.id || undefined,
      dispensaryId: store.id,
      label: eventLabel,
      merchandisingCampaignId: store.merchandisingCampaignId || undefined,
    });
  };

  const { setHoveredStore, clearHoveredStore, selectedStore } =
    useContext(StoreLocatorContext);

  const logoSize =
    store.configurations.imageEnabled && !store.imageUrl ? 128 : 48;
  const showImage = store.configurations.imageEnabled && store.imageUrl;
  const showImageOrLogo = showImage || store.configurations.logoEnabled;
  const tagClasses =
    "z-[1] border border-light-grey rounded bg-white px-xs text-xs font-bold";

  return (
    <li
      id={`store-card-${store.id}`}
      className="h-full"
      onMouseEnter={() => setHoveredStore(store.id)}
      onMouseLeave={clearHoveredStore}
    >
      <TrackImpression
        category={Category.finder}
        label={eventLabel}
        customDimensions={{
          dealId: store.mostRecentActiveDeal?.id || undefined,
          merchandisingCampaignId: store.merchandisingCampaignId || undefined,
        }}
      >
        <div
          className={classnames(
            "flex flex-col items-start justify-between relative h-full w-full",
            "rounded overflow-hidden bg-white p-lg border border-light-grey",
            {
              "shadow-low": store.id === selectedStore?.id,
            },
          )}
        >
          <Link
            href={store.path}
            className="w-full"
            onClick={handleCardClick}
            data-testid="store-card-link"
          >
            <div
              className={classnames("relative w-full", { flex: !showImage })}
            >
              {showImage && (
                <>
                  <Image
                    src={store.imageUrl as string}
                    sizes={[312]}
                    alt={`Cover photo for ${store.name}`}
                    className="h-32 rounded mb-sm"
                    imageClass="object-cover"
                    data-testid="dispensary-card__photo"
                  />
                </>
              )}
              {store.configurations.logoEnabled && (
                <div
                  className={classnames(
                    "rounded overflow-hidden bg-white mb-sm",
                    { "absolute bottom-0 left-0 ml-sm md:block": showImage },
                  )}
                  style={{ minWidth: logoSize, width: logoSize }}
                >
                  {store.logoUrl ? (
                    <Image
                      src={store.logoUrl}
                      sizes={[logoSize]}
                      alt={`${store.name} logo`}
                      ratio="square"
                    />
                  ) : (
                    <div className="p-xs">
                      <StorefrontIcon
                        width={`${logoSize - 8}`}
                        height={`${logoSize - 8}`}
                        className="text-grey"
                        aria-label={`Default logo for ${store.name}`}
                      />
                    </div>
                  )}
                </div>
              )}
              {store.mainTag && showImageOrLogo && (
                <div
                  className={classnames(
                    tagClasses,
                    "top-0 right-0 absolute ml-auto",
                    { "m-sm": showImage },
                  )}
                >
                  {store.mainTag}
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <span className="flex flex-auto truncate-lines truncate-2-lines w-55">
                {store.configurations.leaflyListEnabled && (
                  <AwardBadgeIcon
                    width={24}
                    height={24}
                    className="flex-shrink-0 inline pr-1"
                  />
                )}
                <span className="flex-auto font-bold truncate-lines truncate-2-lines">
                  {store.name}
                </span>
                {store.mainTag && !showImageOrLogo && (
                  <div className="flex-none">
                    <div className={classnames(tagClasses, "ml-sm")}>
                      {store.mainTag}
                    </div>
                  </div>
                )}
              </span>
            </div>

            <div>
              {store.reviewCount > 0 && (
                <StarRating
                  rating={store.reviewRating}
                  ratingCount={store.reviewCount}
                  starWidth={13}
                />
              )}
              {(store.configurations.deliveryEnabled ||
                store.configurations.pickupEnabled) && (
                <div className="flex flex-wrap my-xs">
                  {store.configurations.deliveryEnabled && (
                    <div className="flex items-center bg-leafly-white rounded px-sm mr-sm">
                      <ScooterIcon width="12" height="12" aria-hidden="true" />
                      <span className="ml-xs text-xs font-bold my-0.5">
                        Delivery
                      </span>
                    </div>
                  )}
                  {store.configurations.pickupEnabled && (
                    <PickupLabel
                      pickupReadiness={
                        store.configurations.pickupReadinessEnabled
                      }
                    />
                  )}
                </div>
              )}
              <div className="flex flex-row items-center my-xs text-xs">
                <span className="overflow-hidden">
                  {store.openStatus && (
                    <ScheduleStatus
                      currentStatus={store.openStatus}
                      className="min-h-5 flex-shrink-0 font-normal"
                      preorderConfigured={store.configurations.preorderEnabled}
                      scheduleType={"store"}
                      timeZone={store.timeZone}
                    />
                  )}
                </span>
                {store.openStatus && store.fulfillmentDetails && (
                  <span className="mx-sm flex-shrink-0 leading-none after:content-['•']"></span>
                )}
                {store.fulfillmentDetails && (
                  <span className="flex-shrink-0">
                    {store.fulfillmentDetails}
                  </span>
                )}
              </div>
            </div>
          </Link>

          {dealHref && store.mostRecentActiveDeal && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick();
                push(dealHref);
              }}
              data-testid="deal-enabled-menu-link"
              className="text-xs pt-sm w-full mt-xs border-t border-light-grey"
            >
              {["bogo", "bundle"].includes(
                store.mostRecentActiveDeal?.externalKind ?? "",
              ) ? (
                <OfferLabel
                  label={store.mostRecentActiveDeal.title}
                  textClassName="text-default underline text-left"
                />
              ) : (
                <div className="flex items-start">
                  <div className="bg-notification rounded text-white font-bold mr-sm px-xs flex-shrink-0">
                    {store.mostRecentActiveDeal.discountLabel}
                  </div>
                  <span className="font-bold truncate-lines truncate-2-lines text-left underline">
                    {store.mostRecentActiveDeal.title}
                  </span>
                </div>
              )}
            </button>
          )}

          {store.configurations.ctaEnabled && (
            <CTAButton
              deliveryEnabled={store.configurations.deliveryEnabled}
              handleClick={handleCardClick}
              inStoreCartEnabled={store.configurations.inStoreCartEnabled}
              pickupEnabled={store.configurations.pickupEnabled}
              storePath={store.path}
            />
          )}
        </div>
      </TrackImpression>
    </li>
  );
};

export default StoreCard;
